'use client'

import { useState, useContext, useEffect, useCallback } from 'react'
import { Button } from '@/components/ui/shadcn/button'
import { Label } from '@/components/ui/shadcn/label'
import { Alert, AlertDescription } from '@/components/ui/shadcn/alert'
import { Switch } from '@/components/ui/shadcn/switch'
import { Checkbox } from '@/components/ui/shadcn/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/shadcn/select'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/shadcn/popover'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/shadcn/table'
import { CheckCircle2, ChevronDown, Loader2, Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { AuthContext } from '@/contexts/AuthContext'
import { fetcher } from '@/lib/api/fetch'
import { FUNCTIONAL_TYPES } from '@/lib/functionalTypes'

// Sentinel value: ShadCN Select cannot hold an empty string, so map '' ↔ ANY.
const ANY = '__any__'

const SEVERITIES = [
  { value: ANY, label: 'any' },
  { value: 'informational', label: 'Informational' },
  { value: 'minor', label: 'Minor' },
  { value: 'material', label: 'Material' },
  { value: 'critical', label: 'Critical' },
]

const CRITICALITIES = [
  { value: ANY, label: 'any' },
  { value: 'supporting', label: 'Supporting' },
  { value: 'important', label: 'Important' },
  { value: 'critical', label: 'Critical' },
]

// Must match backend model.ServiceChangeType
const CHANGE_TYPES = [
  { value: 'ai_model_change', label: 'AI Model Change' },
  { value: 'architecture_change', label: 'Architecture Change' },
  { value: 'subcontractor_change', label: 'Subcontractor Change' },
  { value: 'tier_change', label: 'Tier Change' },
  { value: 'vendor', label: 'Vendor' },
  { value: 'system', label: 'System' },
  { value: 'team', label: 'Team' },
  { value: 'policy', label: 'Policy' },
]

const DEFAULT_FUNCTIONAL_TYPE = FUNCTIONAL_TYPES[0].value

/**
 * DriftRoutingPolicyCard — customer-facing settings card for the deterministic
 * drift ownership routing policy (R40). Renders an ordered, editable rule list
 * plus a default functional-type select. Rules are matched top-to-bottom; the
 * first match wins, assigning the drift to the responsible function.
 *
 * Per CLAUDE.md placement rule: customer-facing → Settings, not Admin.
 * Cloned from RevalidationPolicyPanel (R39) for load/save/alert/table mechanics;
 * the per-rule response axis is replaced by functionalType and the
 * evidence-class axis is dropped (routing has no evidence-class dimension).
 *
 * Props: accepts authContext and org as optional props (for testability).
 * In production, falls back to AuthContext if props are omitted.
 */
export default function DriftRoutingPolicyCard({
  authContext: authContextProp,
  org: orgProp,
} = {}) {
  const authContextValue = useContext(AuthContext)
  const authContext = authContextProp || authContextValue
  const org = orgProp || authContext?.user?.organization
  const orgId = org?.id

  const [rules, setRules] = useState([])
  const [defaultFunctionalType, setDefaultFunctionalType] = useState(
    DEFAULT_FUNCTIONAL_TYPE,
  )
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState(null)

  const loadPolicy = useCallback(async () => {
    if (!orgId) return
    setLoading(true)
    setError(null)
    try {
      const res = await fetcher(
        `/api/admin/organization/${orgId}/settings/routing-policy`,
        { method: 'GET' },
        authContext,
      )
      const data = await res.json()
      if (!res.ok) {
        setError(data.error || 'Failed to load routing policy')
        return
      }
      setRules(Array.isArray(data.rules) ? data.rules : [])
      setDefaultFunctionalType(
        data.defaultFunctionalType || DEFAULT_FUNCTIONAL_TYPE,
      )
    } catch (e) {
      setError(e.message || 'Failed to load routing policy')
    } finally {
      setLoading(false)
    }
  }, [orgId, authContext])

  useEffect(() => {
    loadPolicy()
  }, [loadPolicy])

  const updateRule = (index, patch) =>
    setRules(rules.map((r, i) => (i === index ? { ...r, ...patch } : r)))

  const addRule = () =>
    setRules([
      ...rules,
      {
        minSeverity: '',
        minCriticality: '',
        changeTypes: [],
        functionalType: DEFAULT_FUNCTIONAL_TYPE,
        enabled: true,
      },
    ])

  const removeRule = index => setRules(rules.filter((_, i) => i !== index))

  const toggleInList = (index, field, value) => {
    const rule = rules[index]
    const has = rule[field]?.includes(value)
    const next = has
      ? rule[field].filter(v => v !== value)
      : [...(rule[field] || []), value]
    updateRule(index, { [field]: next })
  }

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      const res = await fetcher(
        `/api/admin/organization/${orgId}/settings/routing-policy`,
        {
          method: 'PUT',
          body: JSON.stringify({ rules, defaultFunctionalType }),
        },
        authContext,
      )
      const data = await res.json()
      if (!res.ok) {
        setError(data.error || 'Failed to save routing policy')
        return
      }
      if (Array.isArray(data.rules)) setRules(data.rules)
      setSaved(true)
      setTimeout(() => setSaved(false), 10000)
      toast.success('Drift routing policy saved')
    } catch (e) {
      setError(e.message || 'Failed to save routing policy')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="mb-4">
        <h3 className="text-sm font-semibold">Drift Routing Policy</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          When drift is detected, define which function owns the response. Rules
          are evaluated top-to-bottom; the first match wins. R40.
        </p>
      </div>

      {saved && (
        <Alert className="mb-3 border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-200">
          <CheckCircle2 className="size-4" />
          <AlertDescription>Drift routing policy saved</AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive" className="mb-3">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[140px]">Min severity</TableHead>
                  <TableHead className="w-[140px]">Min criticality</TableHead>
                  <TableHead>Change types</TableHead>
                  <TableHead className="w-[180px]">Functional type</TableHead>
                  <TableHead className="w-[72px]">Enabled</TableHead>
                  <TableHead className="w-[56px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rules.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="py-6 text-center text-sm text-muted-foreground"
                    >
                      No routing rules. Add one to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  rules.map((rule, index) => (
                    <TableRow key={index} data-testid={`routing-rule-${index}`}>
                      <TableCell className="align-top">
                        <Select
                          value={rule.minSeverity || ANY}
                          onValueChange={v =>
                            updateRule(index, {
                              minSeverity: v === ANY ? '' : v,
                            })
                          }
                        >
                          <SelectTrigger size="sm" className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {SEVERITIES.map(s => (
                              <SelectItem key={s.value} value={s.value}>
                                {s.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="align-top">
                        <Select
                          value={rule.minCriticality || ANY}
                          onValueChange={v =>
                            updateRule(index, {
                              minCriticality: v === ANY ? '' : v,
                            })
                          }
                        >
                          <SelectTrigger size="sm" className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {CRITICALITIES.map(c => (
                              <SelectItem key={c.value} value={c.value}>
                                {c.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="align-top whitespace-normal">
                        <MultiSelect
                          label="changes"
                          options={CHANGE_TYPES}
                          selected={rule.changeTypes || []}
                          onToggle={v => toggleInList(index, 'changeTypes', v)}
                          testIdPrefix={`ct-${index}`}
                        />
                      </TableCell>
                      <TableCell className="align-top">
                        <Select
                          value={rule.functionalType || DEFAULT_FUNCTIONAL_TYPE}
                          onValueChange={v =>
                            updateRule(index, { functionalType: v })
                          }
                        >
                          <SelectTrigger size="sm" className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {FUNCTIONAL_TYPES.map(ft => (
                              <SelectItem key={ft.value} value={ft.value}>
                                {ft.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="align-top">
                        <Switch
                          checked={!!rule.enabled}
                          onCheckedChange={v =>
                            updateRule(index, { enabled: v })
                          }
                          aria-label={`Enable rule ${index + 1}`}
                        />
                      </TableCell>
                      <TableCell className="align-top">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-8 text-muted-foreground hover:text-destructive"
                          onClick={() => removeRule(index)}
                          data-testid={`routing-remove-${index}`}
                          aria-label={`Remove rule ${index + 1}`}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Label className="text-xs font-normal text-muted-foreground">
              Default functional type (no rule matched)
            </Label>
            <Select
              value={defaultFunctionalType}
              onValueChange={setDefaultFunctionalType}
            >
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FUNCTIONAL_TYPES.map(ft => (
                  <SelectItem key={ft.value} value={ft.value}>
                    {ft.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="mt-4 flex items-center gap-2">
            <Button
              onClick={handleSave}
              disabled={saving}
              data-testid="routing-save"
            >
              {saving ? (
                <>
                  <Loader2 className="mr-1 size-3.5 animate-spin" />
                  Saving&hellip;
                </>
              ) : (
                'Save'
              )}
            </Button>
            <Button
              variant="outline"
              onClick={addRule}
              data-testid="routing-add"
            >
              <Plus className="size-4" />
              Add rule
            </Button>
          </div>
        </>
      )}
    </div>
  )
}

function MultiSelect({ label, options, selected, onToggle, testIdPrefix }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-8 w-full justify-between font-normal"
        >
          <span className="truncate text-left">
            {selected.length ? `${selected.length} selected` : `Any ${label}`}
          </span>
          <ChevronDown className="size-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-2">
        <div className="flex flex-col gap-1">
          {options.map(opt => {
            const id = `${testIdPrefix}-${opt.value}`
            const checked = selected.includes(opt.value)
            return (
              <label
                key={opt.value}
                htmlFor={id}
                className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
              >
                <Checkbox
                  id={id}
                  checked={checked}
                  onCheckedChange={() => onToggle(opt.value)}
                />
                {opt.label}
              </label>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
}
